import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Trophy, TrendingUp, Coins, Crown, Medal, Award, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { priceToUsd, formatUsdPrice } from "@/lib/priceUtils";

interface CreatorStats {
  id: string | null;
  wallet_address: string;
  display_name: string | null;
  avatar_url: string | null;
  total_sales: number;
  total_earned: number;
  nft_count: number;
}

const ITEMS_PER_PAGE = 3;

const getRankIcon = (rank: number) => {
  switch (rank) {
    case 1:
      return <Crown className="h-5 w-5 text-yellow-400" />;
    case 2:
      return <Medal className="h-5 w-5 text-gray-300" />;
    case 3:
      return <Award className="h-5 w-5 text-amber-600" />;
    default:
      return <span className="text-muted-foreground font-mono">#{rank}</span>;
  }
};

const getRankBadge = (rank: number) => {
  switch (rank) {
    case 1:
      return "gradient-primary text-primary-foreground";
    case 2:
      return "bg-secondary text-secondary-foreground";
    case 3:
      return "bg-accent text-accent-foreground";
    default:
      return "bg-muted text-muted-foreground";
  }
};

export function TopCreatorsLeaderboard() {
  const [sortBy, setSortBy] = useState<"earnings" | "sales">("earnings");
  const [currentPage, setCurrentPage] = useState(0);

  const { data: creators, isLoading } = useQuery({
    queryKey: ["top-creators-leaderboard", sortBy],
    queryFn: async () => {
      const { data: nftStats, error } = await supabase
        .from("nfts")
        .select("creator_wallet, creator_name, sales_count, total_earned")
        .not("creator_wallet", "is", null);

      if (error) throw error;

      const creatorMap = new Map<string, CreatorStats>();
      
      for (const nft of nftStats || []) {
        if (!nft.creator_wallet) continue;
        
        const existing = creatorMap.get(nft.creator_wallet);
        if (existing) {
          existing.total_sales += nft.sales_count || 0;
          existing.total_earned += Number(nft.total_earned) || 0;
          existing.nft_count += 1;
        } else {
          creatorMap.set(nft.creator_wallet, {
            id: null,
            wallet_address: nft.creator_wallet,
            display_name: nft.creator_name,
            avatar_url: null,
            total_sales: nft.sales_count || 0,
            total_earned: Number(nft.total_earned) || 0,
            nft_count: 1,
          });
        }
      }

      const wallets = Array.from(creatorMap.keys());
      if (wallets.length > 0) {
        const { data: profiles } = await supabase
          .from("profiles")
          .select("id, wallet_address, display_name, avatar_url")
          .in("wallet_address", wallets);

        for (const profile of profiles || []) {
          const creator = creatorMap.get(profile.wallet_address);
          if (creator) {
            creator.id = profile.id;
            creator.display_name = profile.display_name || creator.display_name;
            creator.avatar_url = profile.avatar_url;
          }
        }
      }

      let creatorsArray = Array.from(creatorMap.values());
      
      if (sortBy === "earnings") {
        creatorsArray = creatorsArray.filter(c => c.total_earned > 0);
        creatorsArray.sort((a, b) => b.total_earned - a.total_earned);
      } else {
        creatorsArray = creatorsArray.filter(c => c.total_sales > 0);
        creatorsArray.sort((a, b) => b.total_sales - a.total_sales);
      }

      return creatorsArray.slice(0, 12); // Max 12 creators
    },
  });

  // Reset page when switching tabs
  const handleSortChange = (value: string) => {
    setSortBy(value as "earnings" | "sales");
    setCurrentPage(0);
  };

  const totalPages = creators ? Math.ceil(creators.length / ITEMS_PER_PAGE) : 0;
  const paginatedCreators = creators?.slice(
    currentPage * ITEMS_PER_PAGE,
    (currentPage + 1) * ITEMS_PER_PAGE
  ) || [];

  const getProvisionalName = () => "Creator";

  return (
    <Card className="border-primary/20 bg-gradient-to-br from-card to-card/80">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-yellow-500" />
          Top Creators
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs value={sortBy} onValueChange={handleSortChange}>
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="earnings" className="flex items-center gap-1.5">
              <Coins className="h-3.5 w-3.5" />
              Top Earners
            </TabsTrigger>
            <TabsTrigger value="sales" className="flex items-center gap-1.5">
              <TrendingUp className="h-3.5 w-3.5" />
              Top Sellers
            </TabsTrigger>
          </TabsList>

          <TabsContent value={sortBy} className="mt-0">
            {isLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="flex-1">
                      <Skeleton className="h-4 w-24 mb-1" />
                      <Skeleton className="h-3 w-16" />
                    </div>
                    <Skeleton className="h-6 w-16" />
                  </div>
                ))}
              </div>
            ) : paginatedCreators.length > 0 ? (
              <>
                <div className="space-y-2">
                  {paginatedCreators.map((creator, index) => {
                    const rank = currentPage * ITEMS_PER_PAGE + index + 1;
                    const profileLink = creator.id ? `/profile/${creator.id}` : "#";
                    return (
                      <Link
                        key={creator.wallet_address}
                        to={profileLink}
                        className={`flex items-center gap-3 p-2.5 rounded-lg transition-all hover:bg-primary/5 ${
                          rank <= 3 ? "bg-primary/5" : ""
                        }`}
                      >
                        <div className="w-8 flex justify-center">
                          {getRankIcon(rank)}
                        </div>
                        
                        <Avatar className={`h-10 w-10 ring-2 ${
                          rank === 1 ? "ring-primary" : 
                          rank === 2 ? "ring-secondary" : 
                          rank === 3 ? "ring-accent" : "ring-border"
                        }`}>
                          <AvatarImage src={creator.avatar_url || undefined} />
                          <AvatarFallback className="bg-primary/10 text-primary text-xs">
                            {(creator.display_name || creator.wallet_address).slice(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>

                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate text-sm">
                            {creator.display_name || getProvisionalName()}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {creator.nft_count} NFT{creator.nft_count !== 1 ? "s" : ""}
                          </p>
                        </div>

                        <div className="text-right">
                          <Badge className={`${getRankBadge(rank)} text-xs`}>
                            {sortBy === "earnings" 
                              ? formatUsdPrice(priceToUsd(creator.total_earned))
                              : `${creator.total_sales} sales`
                            }
                          </Badge>
                          {sortBy === "earnings" && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              ${creator.total_earned.toFixed(2)} USD • {creator.total_sales} sales
                            </p>
                          )}
                          {sortBy === "sales" && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {formatUsdPrice(priceToUsd(creator.total_earned))} earned
                            </p>
                          )}
                        </div>
                      </Link>
                    );
                  })}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-3 mt-4 pt-4 border-t border-border/30">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                      disabled={currentPage === 0}
                      className="h-8 w-8"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    
                    <div className="flex items-center gap-1.5">
                      {Array.from({ length: totalPages }).map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setCurrentPage(i)}
                          className={`w-2 h-2 rounded-full transition-all ${
                            i === currentPage 
                              ? "bg-primary w-4" 
                              : "bg-muted-foreground/30 hover:bg-muted-foreground/50"
                          }`}
                        />
                      ))}
                    </div>

                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                      disabled={currentPage === totalPages - 1}
                      className="h-8 w-8"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Trophy className="h-12 w-12 mx-auto mb-2 opacity-20" />
                <p>No creators yet</p>
                <p className="text-xs mt-1">Be the first to create an NFT!</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
